import { useEffect } from 'react';

/**
 * Reference-counted body scroll lock.
 *
 * Problem with the old per-modal pattern (`const prev = body.style.overflow;
 * body.style.overflow = 'hidden'; … restore prev`): when modals STACK, the inner
 * modal captures `prev = 'hidden'` (set by the outer). If the outer then unmounts
 * before the inner, the inner restores `'hidden'` on close — leaving the body
 * permanently locked and the page unscrollable until a refresh. Any skipped
 * cleanup (e.g. an error during unmount) leaks the lock the same way.
 *
 * This hook fixes both: a shared counter locks the body while ANY modal is open
 * and restores scrolling to the app default ('') only when the LAST one closes —
 * independent of mount/unmount order. Restoring to '' (not a captured value) also
 * self-heals a body that was left locked by something else.
 */
let lockCount = 0;

export function useBodyScrollLock(active = true) {
  useEffect(() => {
    if (!active) return;
    lockCount += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) document.body.style.overflow = '';
    };
  }, [active]);
}
